import React, {
  useEffect,
  useMemo,
  useState,
  ElementType,
  CSSProperties,
} from 'react';
import compact from 'lodash/compact';
import {
  useMedia,
} from 'core/hooks';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import AirRoundedIcon from '@mui/icons-material/AirRounded';
import BloodtypeRoundedIcon from '@mui/icons-material/BloodtypeRounded';
import DeviceThermostatRoundedIcon from '@mui/icons-material/DeviceThermostatRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import DateRangePicker from 'core/components/DateTimePicker/DateRangePicker';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import Name from 'pages/Dashboard/components/Name';
import Tag from 'core/components/Tag';
import Table from 'core/components/VirtualTable';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  formatVitalMetric,
  formatVitalNumber,
  vitalMetrics,
  formatVitalsHistoryDate,
  getDefaultVitalsDateRange,
  getVitalTrends,
  getVitalsHistoryDate,
  VITALS_EMPTY_STATE_TEXT,
  VitalTrend,
} from 'core/vitals/utils';
import {
  useVitalsHistory,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/hooks';
import {
  VitalMetricHeader,
  VitalTrendChart,
} from 'core/vitals/TrendChart';
import {
  VitalMetric,
  VitalMetricId,
  TrendChartPoint,
  VitalsHistoryItem,
} from 'core/vitals/types';
import {
  Props,
} from 'pages/Dashboard/pages/Charts/pages/Details/types/tabContentProps';
import {
  formatDateTime,
} from 'utils/date';

type ViewMode = 'chart' | 'table';

type MetricOptionProps = {
  trend: VitalTrend;
  selected: boolean;
  onSelect: (metricId: VitalMetricId) => void;
};

type LatestVitalsProps = {
  data: VitalsHistoryItem[];
};

type VitalsTableProps = {
  data: VitalsHistoryItem[];
};

type SparklineChartProps = {
  metric: VitalMetric;
  points: TrendChartPoint[];
  x: (index: number) => number;
  y: (value: number) => number;
};

function getXAxisPoints(pointCount: number, width: number, padding: number) {
  return (index: number) => (
    pointCount === 1 ? width / 2 : padding + (index * (width - (padding * 2))) / (pointCount - 1)
  );
}

function getYAxisPoints(values: number[], height: number, padding: number) {
  if (values.length === 0) {
    return () => height / 2;
  }

  const min = Math.min(...values);
  const range = Math.max(...values) - min || 1;

  return (value: number) => height - padding - ((value - min) / range) * (height - (padding * 2));
}

const viewTabs = [{
  key: 'chart',
  label: (
    <span className="flex items-center gap-1.5">
      <ShowChartRoundedIcon className="!h-4 !w-4" />
      Trends
    </span>
  ),
}, {
  key: 'table',
  label: (
    <span className="flex items-center gap-1.5">
      <TableChartOutlinedIcon className="!h-4 !w-4" />
      History
    </span>
  ),
}];

const sparklineDimensions = {
  width: 112,
  height: 48,
  padding: 3,
};

const vitalHeaderIcons: Record<VitalMetricId, ElementType> = {
  'blood-pressure': BloodtypeRoundedIcon,
  'heart-rate': FavoriteBorderRoundedIcon,
  'respiratory-rate': AirRoundedIcon,
  temperature: DeviceThermostatRoundedIcon,
  'oxygen-saturation': WaterDropRoundedIcon,
};

function getMetricAccentColor(metric: VitalMetric): string {
  return metric.id === 'blood-pressure' ? '#b91c1c' : metric.series[0].color;
}

function getMetricChange(
  metric: VitalMetric,
  latest?: VitalsHistoryItem,
  previous?: VitalsHistoryItem,
): string | null {
  if (isNil(latest) || isNil(previous)) {
    return null;
  }

  const deltas = metric.series.map(({ key }) => {
    const current = latest[key];
    const prior = previous[key];

    if (typeof current !== 'number' || typeof prior !== 'number') {
      return null;
    }

    const delta = current - prior;
    return `${delta >= 0 ? '+' : ''}${formatVitalNumber(delta, key)}`;
  }).filter((value): value is string => value !== null);

  return deltas.length > 0 ? `${deltas.join('/')} from previous visit` : null;
}

function LatestVitals({ data }: LatestVitalsProps) {
  const latest = data.at(-1);
  const previous = data.at(-2);
  const recorder = latest?.updatedByUserName?.indexByLastNameWithCredentials?.trim();
  const byline = latest
    ? `Updated ${formatVitalsHistoryDate(latest)}${recorder ? ` by ${recorder}` : ''}`
    : VITALS_EMPTY_STATE_TEXT.noVitalsForDateRange;

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <div className="text-sm font-semibold text-gray-950">Latest</div>
        <div className="text-[11px] text-gray-500">{byline}</div>
      </div>
      <div className="vitals-latest-metrics mt-2 grid grid-cols-5 divide-x divide-gray-200 border-t border-gray-200 pt-3">
        {vitalMetrics.map((metric) => (
          <div
            key={metric.id}
            className="vitals-latest-metric flex justify-center px-4 py-1"
          >
            <div className="vitals-latest-metric-body grid w-fit grid-rows-[auto_auto_auto] gap-y-1">
              <VitalMetricHeader metric={metric} showUnit={false} showSeries={false} />
              <div className="flex items-center py-2 vitals-latest-metric-value-container">
                <div className="flex min-w-0 items-baseline gap-1">
                  <span className="text-lg font-semibold tracking-tight text-gray-950 vitals-latest-metric-value">
                    {latest ? formatVitalMetric(latest, metric) : DEFAULT_EMPTY_VALUE}
                  </span>
                  <span className="text-xs text-gray-500">{metric.unit}</span>
                </div>
              </div>
              <div className="h-4 text-[11px] font-medium text-gray-700 vitals-latest-vitals-change">
                {getMetricChange(metric, latest, previous)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShadedBandSparkline({ metric, points, x, y }: SparklineChartProps) {
  const [systolicSeries, diastolicSeries] = metric.series;
  const systolicPoints = points.map((point, index) => (
    `${x(index)},${y(point[systolicSeries.key] ?? 0)}`
  )).join(' ');
  const diastolicPoints = points.map((point, index) => (
    `${x(index)},${y(point[diastolicSeries.key] ?? 0)}`
  )).join(' ');
  const bandPoints = [
    systolicPoints,
    points.slice().reverse().map((point, index) => (
      `${x(points.length - index - 1)},${y(point[diastolicSeries.key] ?? 0)}`
    )).join(' '),
  ].join(' ');

  return (
    <>
      <polygon points={bandPoints} fill={systolicSeries.color} fillOpacity="0.16" />
      <polyline
        points={systolicPoints}
        fill="none"
        stroke={systolicSeries.color}
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points={diastolicPoints}
        fill="none"
        stroke={diastolicSeries.color}
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.map((point, index) => (
        <g key={point.fullLabel}>
          <circle
            cx={x(index)}
            cy={y(point[systolicSeries.key] ?? 0)}
            r="2.5"
            fill={systolicSeries.color}
          />
          <circle
            cx={x(index)}
            cy={y(point[diastolicSeries.key] ?? 0)}
            r="2.5"
            fill={diastolicSeries.color}
          />
        </g>
      ))}
    </>
  );
}

function LineSparkline({ metric, points, x, y }: SparklineChartProps) {
  return metric.series.map(({ key, color }) => {
    const seriesPoints = points.map((point, index) => `${x(index)},${y(point[key] ?? 0)}`).join(' ');

    return (
      <g key={key}>
        <polyline
          points={seriesPoints}
          fill="none"
          stroke={color}
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((point, index) => (
          <circle
            key={point.fullLabel}
            cx={x(index)}
            cy={y(point[key] ?? 0)}
            r="2.5"
            fill={color}
          />
        ))}
      </g>
    );
  });
}

function Sparkline({ trend }: Pick<MetricOptionProps, 'trend'>) {
  const {
    width,
    height,
    padding,
  } = sparklineDimensions;
  const points = trend.points.slice(-4);
  const values = points.flatMap((point) => trend.metric.series.map(({ key }) => point[key] ?? 0));
  const x = getXAxisPoints(points.length, width, padding);
  const y = getYAxisPoints(values, height, padding);

  const props = {
    metric: trend.metric,
    points,
    x,
    y,
  };

  return (
    points.length === 0
      ? <div className="h-px w-full bg-gray-200" />
      : (
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          className="h-full w-full overflow-hidden"
          aria-hidden="true"
        >
          {trend.metric.id === 'blood-pressure'
            ? <ShadedBandSparkline {...props} />
            : <LineSparkline {...props} />}
        </svg>
      )
  );
}

function MetricOption({ trend, selected, onSelect }: MetricOptionProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => { onSelect(trend.metric.id); }}
      className={clsx(
        'vitals-metric-option group relative grid min-h-[3.75rem] w-full grid-cols-[minmax(0,1fr)_4.75rem] items-center gap-2.5 overflow-hidden rounded-md border px-3 py-2 text-left transition duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
        {
          'border-gray-300 bg-white shadow-[0_10px_25px_-20px_rgba(15,23,42,0.65)]': selected,
          'border-gray-200 bg-transparent hover:shadow-[inset_3px_0_0_var(--metric-color)]': !selected,
        },
      )}
      style={{
        '--metric-color': getMetricAccentColor(trend.metric),
        ...(selected ? {
          boxShadow: `inset 3px 0 0 ${getMetricAccentColor(trend.metric)}`,
          backgroundImage: `linear-gradient(135deg, ${getMetricAccentColor(trend.metric)}1f 0%, #ffffff 58%, #ffffff 100%)`,
        } : {}),
      } as CSSProperties}
    >
      <div className="min-w-0 overflow-hidden">
        <VitalMetricHeader metric={trend.metric} showUnit={false} showSeries={false} />
      </div>
      <div className="h-9 min-w-0 self-center overflow-hidden vitals-metric-option-sparkline">
        <Sparkline trend={trend} />
      </div>
      <span className="sr-only">Select {trend.metric.title}</span>
    </button>
  );
}

type UpdatedCellProps = {
  row: VitalsHistoryItem;
};

function UpdatedCell({ row }: UpdatedCellProps) {
  const src = useUserProfilePicturePath(row.updatedByUserId ?? undefined);

  return (
    <Name
      {...row.updatedByUserName}
      name={row.updatedByUserName?.indexByLastNameWithCredentials ?? DEFAULT_EMPTY_VALUE}
      image={{ src }}
      labelClassName="!overflow-visible !text-clip !whitespace-normal break-words"
    >
      <span className="text-xs text-gray-500">{formatVitalsHistoryDate(row)}</span>
    </Name>
  );
}

const vitalsHistoryColumns: ColumnDef<VitalsHistoryItem>[] = [{
  id: 'encounterDate',
  header: 'Date',
  size: 100,
  cell: ({ row: { original } }) => (
    <span>{formatDateTime(original.encounterDateTime ?? '')}</span>
  ),
}, {
  id: 'visit',
  header: 'Visit',
  size: 160,
  minSize: 160,
  meta: { autoSized: true },
  cell: ({ row: { original } }) => (
    <div className="flex flex-col gap-1.5 py-1">
      <Tag
        className="w-fit max-w-[140px] truncate"
        tagName={original.typeOfVisitDescription ?? DEFAULT_EMPTY_VALUE}
        tooltip={original.typeOfVisitDescription ?? undefined}
      />
      <div className="text-xs text-gray-500">{original.reasonForVisit ?? DEFAULT_EMPTY_VALUE}</div>
    </div>
  ),
}, ...vitalMetrics.map<ColumnDef<VitalsHistoryItem>>((metric) => ({
  id: metric.id,
  multiLineHeader: true,
  minSize: 150,
  header: () => {
    const Icon = vitalHeaderIcons[metric.id];

    return (
      <span className="flex w-full items-center justify-end gap-2">
        <Icon className="!h-4 !w-4 shrink-0 self-center" style={{ color: getMetricAccentColor(metric) }} />
        <span className="w-min whitespace-normal text-left leading-tight">{metric.title}</span>
      </span>
    );
  },
  cell: ({ row: { original } }) => (
    <div className="flex w-full items-baseline justify-end font-mono tabular-nums">
      <span className="whitespace-nowrap text-right text-base">{formatVitalMetric(original, metric)}</span>
      <span className="pl-1 text-xs text-gray-500">{metric.unit}</span>
    </div>
  ),
})), {
  id: 'recorded',
  header: 'Updated',
  size: 200,
  minSize: 200,
  className: 'ml-8',
  cell: ({ row: { original } }) => <UpdatedCell row={original} />,
}];

function VitalsTable({ data }: VitalsTableProps) {
  const { requestMedia } = useMedia();

  useEffect(() => {
    const userIds = compact(data.map((item) => item.updatedByUserId ?? undefined));
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }, [data, requestMedia]);

  return (
    <div className="flex flex-col flex-auto">
      <h4 className="text-sm font-semibold text-gray-400 mb-4">Vitals History</h4>
      <Table<VitalsHistoryItem>
        getRowId={(row) => `${row.encounterId ?? 'reading'}-${getVitalsHistoryDate(row)}`}
        columns={vitalsHistoryColumns}
        data={[...data].reverse()}
        classes={{
          header: '!justify-start',
          outerRow: '!w-full',
          row: '!justify-start',
          outerColumn: (column) => (column.id === 'recorded' ? 'ml-8' : ''),
          windowsClassName: '!w-full',
          scrollbarGutter: 'initial',
        }}
      />
    </div>
  );
}

export default function Vitals({ patient }: Props) {
  const [viewMode, setViewMode] = useState<ViewMode>('chart');
  const [dateRange, setDateRange] = useState(getDefaultVitalsDateRange);
  const [selectedMetricId, setSelectedMetricId] = useState<VitalMetricId>(vitalMetrics[0].id);
  const {
    data,
    isError,
    isLoading,
  } = useVitalsHistory(patient?.patientId ?? 0, dateRange);
  const trends = useMemo(() => getVitalTrends(data ?? []), [data]);
  const selectedTrend = trends.find((trend) => trend.metric.id === selectedMetricId) ?? trends[0];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <DateRangePicker
          value={dateRange}
          onChange={(nextDateRange) => {
            if (nextDateRange) {
              setDateRange(nextDateRange);
            }
          }}
        />
        <div className="flex justify-end">
          <NavigationTabs
            className="!min-h-[initial] !rounded-md !bg-gray-100 !p-1 [&_.MuiTabs-indicator]:!hidden [&_.MuiTabs-flexContainer]:!gap-0 [&_.MuiTab-root.Mui-selected]:!rounded"
            classes={{
              tab: '!border-0 !font-semibold !text-gray-500 hover:!bg-gray-200/70',
              active: '!bg-white !text-blue-600 !shadow-sm hover:!bg-white',
            }}
            tabs={viewTabs}
            activeIndex={viewMode === 'chart' ? 0 : 1}
            onClick={(index) => {
              setViewMode(index === 0 ? 'chart' : 'table');
            }}
          />
        </div>
      </div>

      {isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Failed to load vitals history for this patient.
        </div>
      )}

      {!isError && viewMode === 'table' && isLoading && (
        <div className="flex flex-auto justify-start items-center text-xs">
          Loading vitals history...
        </div>
      )}

      {!isError && viewMode === 'table' && !isLoading && (data?.length ?? 0) === 0 && (
        <div className="py-12 text-center text-sm text-gray-500">
          {VITALS_EMPTY_STATE_TEXT.noVitalsForDateRange}
        </div>
      )}

      {!isError && viewMode === 'table' && !isLoading && (data?.length ?? 0) > 0 && (
        <VitalsTable data={data ?? []} />
      )}

      {!isError && viewMode === 'chart' && isLoading && (
        <div className="py-12 text-center text-sm text-gray-500">Loading vitals history...</div>
      )}

      {!isError && viewMode === 'chart' && !isLoading && !isNil(selectedTrend) && (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <LatestVitals data={data ?? []} />
          <div className="flex min-h-0 flex-1 flex-col gap-4 border-t border-gray-200 pt-5">
            <div className="text-sm font-semibold text-gray-950">Trends</div>
            <div className="vitals-trends-layout grid min-h-0 flex-1 grid-cols-5 gap-8">
              <section className="vitals-trends-chart col-span-4 flex min-h-[400px] min-w-0 flex-col overflow-hidden rounded-md border border-gray-200 bg-white px-3 py-1.5 shadow-[0_16px_36px_-30px_rgba(15,23,42,0.55)]">
                <VitalMetricHeader metric={selectedTrend.metric} />
                <div className="mt-7 min-h-0 flex-1">
                  <VitalTrendChart
                    metric={selectedTrend.metric}
                    points={selectedTrend.points}
                    axes
                    fillArea={false}
                    emptyText={VITALS_EMPTY_STATE_TEXT.noTrendDataForDateRange}
                  />
                </div>
              </section>
              <aside className="vitals-trends-metrics col-span-1 flex flex-col gap-4">
                {trends.map((trend) => (
                  <MetricOption
                    key={trend.metric.id}
                    trend={trend}
                    selected={trend.metric.id === selectedMetricId}
                    onSelect={setSelectedMetricId}
                  />
                ))}
              </aside>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
